import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BondWizard } from '../components/BondWizard';
import { OTCWizard } from '../components/OTCWizard';
import { BetWizard } from '../components/BetWizard';
import { FINANCIAL_PRODUCTS } from '../config';
import { useTrinityContext } from '../contexts/TrinityContext';

export function Create() {
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const navigate = useNavigate();
  const { selectedEntity } = useTrinityContext();

  const handleProductClick = (productId: string) => {
      if (!FINANCIAL_PRODUCTS.find(p => p.id === productId)?.enabled) return;
    
    if (productId === 'bonds' || productId === 'otc') {
      setSelectedProduct(productId);
    } else {
      setSelectedProduct(productId);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <h1 className="text-4xl font-bold mb-0 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">Create Your Own...</h1>
        <p className="text-base text-gray-400/40">Launch Your Own DeFi Products</p>
        <p className="text-base text-gray-400/40">*Disclaimer: This is a toolkit of templates for smart contract deployment.  You are the owner of the contracts deployed here and are responsible for all logic, UI, execution, security and regulations.</p>
      </motion.div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-12">
        {FINANCIAL_PRODUCTS.map((product) => (
          <motion.div
            key={product.id}
            className="relative aspect-square"
          >
            <motion.button
              onClick={() => handleProductClick(product.id)}
              disabled={!product.enabled}
              className={`absolute inset-0 w-full h-full flex flex-col items-center justify-center rounded-3xl transition-all duration-300 overflow-hidden ${
                product.enabled
                  ? 'bg-gradient-to-br from-blue-500/40 via-purple-500/40 to-pink-500/40 hover:from-blue-500/50 hover:via-purple-500/50 hover:to-pink-500/50'
                  : 'bg-gray-900/30 cursor-not-allowed'
              }`}
              whileHover={product.enabled ? { scale: 1.05 } : undefined}
              whileTap={product.enabled ? { scale: 0.95 } : undefined}
            >
              {/* Background gradient layer */}
              <div className={`absolute inset-0 ${
                product.enabled 
                  ? 'bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-pink-600/20' 
                  : ''
              }`} />

              {/* Icon container */}
              <div className="relative z-10 flex items-center justify-center w-4/5 h-4/5">
                <product.icon
                  size="100%"
                  weight={product.enabled ? "regular" : "light"}
                  className={product.enabled 
                    ? "text-white opacity-90" 
                    : "text-gray-500 opacity-50"
                  }
                />
              </div>

              {/* Disabled overlay */}
              {!product.enabled && (
                <div className="absolute inset-0 bg-black/40" />
              )}
            </motion.button>

            {/* Label */}
            <div className="absolute -bottom-8 left-0 right-0 text-center">
              <span className={`text-base font-medium ${product.enabled ? 'text-white/80' : 'text-gray-500'}`}>
                {product.name}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {selectedProduct === 'bonds' && (
          <BondWizard 
            onClose={() => setSelectedProduct(null)} 
            entityAddress={selectedEntity as `0x${string}`}
          />
        )}
        {selectedProduct === 'otc' && (
          <OTCWizard 
            onClose={() => setSelectedProduct(null)} 
            entityAddress={selectedEntity as `0x${string}`}
          />
        )}
        {selectedProduct === 'bet' && (
          <BetWizard 
            onClose={() => setSelectedProduct(null)} 
            entityAddress={selectedEntity as `0x${string}`}
          />
        )}
      </AnimatePresence>
    </div>
  );
} 